import { type VoidApprovalListItem, type VoidApprovalStatus } from '@utazzo/shared';
import { UzBadge, UzButton, UzDialog, UzTextarea } from '@utazzo/ui';
import { Check, ExternalLink, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { fmtMoney } from '../components/price-visibility';
import { useApproveVoid, useRejectVoid, useVoidApprovals } from '../use-void-approvals';

import { DataGrid, type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { PageShell } from '@/components/page-shell';
import { useCurrentUser } from '@/features/auth/use-auth';
import { useDataGridState } from '@/hooks/use-data-grid-state';

/**
 * Void-a-won-deal approval queue. An agent raises a request to reopen a won
 * deal for an itinerary amendment; it routes through the same reviewer
 * hierarchy as reprice approvals (ADMIN all, SALES_LEAD assigned, SALES_AGENT
 * their own, read-only). Approving runs the server-side executor.
 */

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'SUPERSEDED', label: 'Superseded' },
] as const;

const DEFAULT_FILTERS: Readonly<Record<string, string>> = { status: 'PENDING' };

const STATIC_FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-36',
  },
];

const parseStatusFilter = (value: string | undefined): VoidApprovalStatus | undefined => {
  if (
    value === 'PENDING' ||
    value === 'APPROVED' ||
    value === 'REJECTED' ||
    value === 'SUPERSEDED'
  ) {
    return value;
  }
  return undefined;
};

const formatWhen = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const canReviewRow = (
  row: VoidApprovalListItem,
  me: { id: string; role: string } | null | undefined,
): boolean => {
  if (!me) return false;
  if (me.role === 'ADMIN') return true;
  if (me.role === 'SALES_LEAD' && row.assignedReviewer?.id === me.id) return true;
  return false;
};

export const VoidApprovalsPage = (): React.JSX.Element => {
  const navigate = useNavigate();
  const me = useCurrentUser();
  const isAdmin = me?.role === 'ADMIN';
  const isReviewer = me?.role === 'ADMIN' || me?.role === 'SALES_LEAD';

  const gridState = useDataGridState({ filterIds: ['status'], defaultFilters: DEFAULT_FILTERS });
  const { state } = gridState;
  const statusFilter = parseStatusFilter(state.filters.status as string | undefined);
  const searchTerm = state.q.trim() || undefined;

  const listQuery = useVoidApprovals({
    page: state.page,
    pageSize: state.pageSize,
    status: statusFilter,
    search: searchTerm,
    sortDir: state.sortDir,
  });
  const approve = useApproveVoid();
  const reject = useRejectVoid();

  const [dialogTarget, setDialogTarget] = useState<{
    row: VoidApprovalListItem;
    action: 'approve' | 'reject';
  } | null>(null);
  const [remark, setRemark] = useState('');

  const closeDialog = (): void => {
    setDialogTarget(null);
    setRemark('');
  };

  const submitting = approve.isPending || reject.isPending;
  const remarkRequired = dialogTarget?.action === 'reject';
  const remarkTrimmed = remark.trim();
  const canSubmit = !remarkRequired || remarkTrimmed.length > 0;

  const submitReview = async (): Promise<void> => {
    if (!dialogTarget || !canSubmit) return;
    try {
      if (dialogTarget.action === 'approve') {
        await approve.mutateAsync({ id: dialogTarget.row.id, remark: remarkTrimmed || undefined });
      } else {
        await reject.mutateAsync({ id: dialogTarget.row.id, remark: remarkTrimmed });
      }
      closeDialog();
    } catch {
      // Error toast already shown by the mutation hooks.
    }
  };

  const rows = listQuery.data?.data ?? [];
  const pagination = listQuery.data
    ? {
        page: listQuery.data.pagination.page,
        pageSize: listQuery.data.pagination.pageSize,
        totalItems: listQuery.data.pagination.totalItems,
      }
    : null;

  const columns = useMemo((): ReadonlyArray<DataGridColumn<VoidApprovalListItem>> => {
    const cols: DataGridColumn<VoidApprovalListItem>[] = [
      {
        id: 'itinerary',
        header: 'Itinerary / lead',
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">
              {row.itineraryTitle ?? 'Untitled itinerary'}
            </span>
            <span className="text-xs text-zinc-500">
              {row.leadReference ?? 'No lead'}
              {row.customerName ? ` · ${row.customerName}` : ''}
            </span>
          </div>
        ),
      },
      {
        id: 'reason',
        header: 'Reason',
        cell: (row) => (
          <span className="line-clamp-2 text-sm text-zinc-700">{row.requestReason}</span>
        ),
      },
      {
        id: 'requestedBy',
        header: 'Requested by',
        width: '150px',
        cell: (row) => <span className="text-sm text-zinc-700">{row.requestedBy.fullName}</span>,
      },
      {
        id: 'assignedReviewer',
        header: 'Requested to',
        width: '150px',
        cell: (row) => (
          <span className="text-sm text-zinc-700">
            {row.assignedReviewer ? (
              row.assignedReviewer.fullName
            ) : (
              <span className="text-zinc-500">Admin (any)</span>
            )}
          </span>
        ),
      },
      {
        id: 'paidAmountPaise',
        header: 'Paid (retained)',
        width: '130px',
        cell: (row) => (
          <span className="text-sm text-zinc-700 tabular-nums">
            {row.paidAmountPaise && row.paidAmountPaise !== '0'
              ? (fmtMoney(row.paidAmountPaise) ?? '-')
              : '-'}
          </span>
        ),
      },
      {
        id: 'createdAt',
        header: 'Requested',
        width: '140px',
        cell: (row) => <span className="text-sm text-zinc-600">{formatWhen(row.createdAt)}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        width: '110px',
        cell: (row) => {
          const tone: 'success' | 'destructive' | 'secondary' =
            row.status === 'APPROVED'
              ? 'success'
              : row.status === 'REJECTED'
                ? 'destructive'
                : 'secondary';
          return (
            <UzBadge tone={tone} pill>
              {row.status}
            </UzBadge>
          );
        },
      },
    ];

    cols.push({
      id: 'actions',
      header: '',
      width: '220px',
      align: 'right',
      truncate: false,
      resizable: false,
      alwaysVisible: true,
      cell: (row) => (
        <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
          <UzButton
            size="sm"
            variant="ghost"
            onClick={() => navigate(`/itineraries/${row.itineraryId}`)}
            title="Open itinerary"
          >
            <ExternalLink className="h-4 w-4" />
          </UzButton>
          {row.status === 'PENDING' && canReviewRow(row, me) && (
            <>
              <UzButton size="sm" onClick={() => setDialogTarget({ row, action: 'approve' })}>
                <Check className="mr-1 h-4 w-4" />
                Approve
              </UzButton>
              <UzButton
                size="sm"
                variant="outline"
                onClick={() => setDialogTarget({ row, action: 'reject' })}
              >
                <X className="mr-1 h-4 w-4" />
                Reject
              </UzButton>
            </>
          )}
        </div>
      ),
    });

    return cols;
  }, [me, navigate]);

  const description = isAdmin
    ? 'Every request to void a won deal for an amendment - pending, approved and rejected.'
    : isReviewer
      ? 'Void requests from your direct reports that need approval.'
      : 'The void requests you have submitted - pending, approved and rejected.';

  const emptyTitle = searchTerm
    ? `No void requests match "${state.q.trim()}"`
    : isReviewer
      ? 'No void requests'
      : 'You have no void requests';
  const emptyDescription = searchTerm
    ? 'Search covers the itinerary title, lead reference, customer name and requester.'
    : 'A request appears here when a won deal needs to be voided for an itinerary amendment.';

  return (
    <PageShell title="Void approvals" description={description}>
      <DataGrid
        gridId="itinerary-void-approvals"
        data={rows}
        columns={columns}
        filters={STATIC_FILTERS}
        getRowId={(row) => row.id}
        isLoading={listQuery.isLoading}
        isError={listQuery.isError}
        onRetry={() => void listQuery.refetch()}
        pagination={pagination}
        state={state}
        onStateChange={gridState}
        onRowClick={(row) => navigate(`/itineraries/${row.itineraryId}`)}
        searchPlaceholder="Search itinerary, lead ref or requester"
        emptyTitle={emptyTitle}
        emptyDescription={emptyDescription}
        tableLayout="auto"
      />

      <UzDialog
        open={dialogTarget !== null}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
        title={`${dialogTarget?.action === 'approve' ? 'Approve void' : 'Reject void'} for ${
          dialogTarget?.row.itineraryTitle ?? 'this itinerary'
        }`}
        description={
          dialogTarget?.action === 'approve'
            ? 'Approving voids the contract (CRM + e-sign), un-wins the lead to its previous active status, reverts the itinerary to Sent to customer, cancels the payment plan, and emails the customer. Any paid amount is retained for the new itinerary.'
            : 'Rejecting leaves the deal exactly as it is. A remark is required so the requester knows why.'
        }
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <UzButton variant="ghost" onClick={closeDialog} disabled={submitting}>
              Cancel
            </UzButton>
            <UzButton
              variant={dialogTarget?.action === 'reject' ? 'outline' : 'default'}
              onClick={() => void submitReview()}
              loading={submitting}
              disabled={!canSubmit}
            >
              {dialogTarget?.action === 'approve' ? 'Confirm void' : 'Confirm rejection'}
            </UzButton>
          </div>
        }
      >
        {dialogTarget && (
          <div className="space-y-3 text-sm text-zinc-700">
            <p>
              Reason: <strong>{dialogTarget.row.requestReason}</strong>
            </p>
            {dialogTarget.row.paidAmountPaise && dialogTarget.row.paidAmountPaise !== '0' ? (
              <p>
                Already paid (retained for the new itinerary):{' '}
                <strong>{fmtMoney(dialogTarget.row.paidAmountPaise) ?? '-'}</strong>
              </p>
            ) : null}
            <p>
              Requested by <strong>{dialogTarget.row.requestedBy.fullName}</strong>
            </p>
            <UzTextarea
              name="reviewRemark"
              label={remarkRequired ? 'Reason for rejection (required)' : 'Remark (optional)'}
              rows={3}
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              disabled={submitting}
            />
          </div>
        )}
      </UzDialog>
    </PageShell>
  );
};
